import React, {useState, useEffect} from 'react';
import { Link } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import QuizIcon from '@mui/icons-material/Quiz';
import { Button, Chip, IconButton, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';

import { adminGetTheoryStages } from "../../api/Api";
import { useColors } from '../../theme/ColorModeContext';
import PageContainer from '../../shared/PageContainer';
import GlowCard from '../../shared/GlowCard';
import CyberpunkTitle from '../../shared/CyberpunkTitle';

interface PropsAdminTheoryStages {
    token: string;
}

interface TheoryStage {
    id: string;
    category: string;
    slug: string;
    title: string;
    order_index: number;
    points: number;
    active: boolean;
}

const AdminTheoryStages = (props: PropsAdminTheoryStages) => {
    const colors = useColors();
    const [stages, setStages] = useState<TheoryStage[]>([]);
    const token = props.token;

    useEffect(() => {
        const fetchData = async () => {
            const data = await adminGetTheoryStages(token);
            if (data) setStages(data.stages);
        }
        fetchData();
    }, []);

    return (
        <PageContainer>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                <CyberpunkTitle
                    title="Theory Stages"
                    subtitle={`${stages.length} ENTRIES`}
                    mb="0"
                />
                <Link to="/admin/theory/stages/add" style={{ textDecoration: 'none' }}>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        sx={{
                            borderColor: colors.accent.cyan,
                            color: colors.accent.cyan,
                            fontFamily: '"Share Tech Mono", monospace',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            padding: '8px 20px',
                            borderRadius: '6px',
                            '&:hover': {
                                backgroundColor: colors.accent.cyanGlow,
                                borderColor: colors.accent.cyan,
                                boxShadow: `0 0 16px ${colors.accent.cyan}44`,
                            },
                        }}
                    >
                        Add Stage
                    </Button>
                </Link>
            </Box>

            <GlowCard glowOnHover={false}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{
                                borderBottom: `1px solid ${colors.border.accent}`,
                                '& th': {
                                    fontFamily: '"Share Tech Mono", monospace',
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                    color: colors.accent.cyan,
                                    borderBottom: 'none',
                                    padding: '14px 16px',
                                    backgroundColor: 'transparent',
                                }
                            }}>
                                <TableCell>#</TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell>Category</TableCell>
                                <TableCell>Slug</TableCell>
                                <TableCell align="right">Order</TableCell>
                                <TableCell align="right">Points</TableCell>
                                <TableCell align="center">Active</TableCell>
                                <TableCell align="center">Questions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {stages.sort((a, b) => a.order_index - b.order_index).map((stage, idx) => (
                                <TableRow
                                    key={stage.id}
                                    sx={{
                                        '&:last-child td': { border: 0 },
                                        borderBottom: `1px solid ${colors.border.subtle}`,
                                        '& td': { borderBottom: 'none', padding: '12px 16px' },
                                    }}
                                >
                                    <TableCell>
                                        <Typography sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.75rem', color: colors.text.muted }}>
                                            {String(idx + 1).padStart(2, '0')}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Link style={{ textDecoration: 'none' }} to={`/admin/theory/stage/${stage.id}`}>
                                            <Typography sx={{
                                                fontFamily: '"Orbitron", sans-serif',
                                                fontWeight: 600,
                                                color: colors.text.primary,
                                                '&:hover': { color: colors.accent.cyan },
                                            }}>
                                                {stage.title}
                                            </Typography>
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={stage.category}
                                            size="small"
                                            sx={{
                                                fontFamily: '"Share Tech Mono", monospace',
                                                fontSize: '0.65rem',
                                                fontWeight: 600,
                                                backgroundColor: colors.accent.purpleGlow,
                                                color: colors.accent.purple,
                                                border: `1px solid ${colors.accent.purple}44`,
                                                height: '22px',
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.78rem', color: colors.text.secondary }}>
                                            {stage.slug}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">{stage.order_index}</TableCell>
                                    <TableCell align="right">
                                        <Typography sx={{ fontFamily: '"Share Tech Mono", monospace', fontWeight: 700, color: colors.accent.cyan, fontSize: '0.9rem' }}>
                                            {stage.points}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        {stage.active
                                            ? <CheckCircleIcon sx={{ color: colors.accent.green, fontSize: '1.2rem' }} />
                                            : <CancelIcon sx={{ color: colors.text.muted, fontSize: '1.2rem' }} />
                                        }
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Manage questions">
                                            <Link to={`/admin/theory/stage/${stage.id}/questions`}>
                                                <IconButton size="small" sx={{ color: colors.accent.cyan }}>
                                                    <QuizIcon fontSize="small" />
                                                </IconButton>
                                            </Link>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {!stages.length && (
                    <Box sx={{ textAlign: 'center', padding: '60px 0' }}>
                        <CircularProgress size={40} sx={{ color: colors.accent.cyan }} />
                    </Box>
                )}
            </GlowCard>
        </PageContainer>
    );
};

export default AdminTheoryStages;
